import { tagged } from "@mirage/service-logging";

import type {
  DataStorage,
  StoredConversation,
} from "@mirage/service-conversation/service/conversationsSync/types";
import type { GuaranteedKVStorage } from "@mirage/storage";

const logger = tagged("conversationsSync/adapters");

export interface KVStorageShape {
  conversations: {
    rows: { [conversationID: string]: StoredConversation };
    /* provide a way to identify when the storage schema has changed */
    version: 1;
  };
  // TODO: we do not currently persist metadata to storage -- though we may add this in the future
  // metadatas: {
  //   rows: ConversationMetadata[];
  //   /* provide a way to identify when the storage schema has changed */
  //   version: 1;
  // };
}

/**
 * Create a storage adapter for the conversations sync service using a KV storage.
 */
export function createStorageAdapter(
  store: GuaranteedKVStorage<KVStorageShape>,
): DataStorage {
  const loadConversations: DataStorage["loadConversations"] = async () => {
    const savedConversations = await store.get("conversations");
    if (savedConversations?.version === 1) {
      logger.debug(
        "loaded conversations",
        Object.keys(savedConversations.rows).length,
      );
      const fixedRows = fixConversationsDates(savedConversations.rows);
      return getRecentConversations(fixedRows);
    } else {
      // Ignore old data if the version is not >=1
      return {};
    }
  };

  const saveConversation: DataStorage["saveConversation"] = async (
    conversationID: string,
    conversation: StoredConversation,
  ) => {
    const savedConversations = await loadConversations();
    const updatedConversations = {
      ...savedConversations,
      [conversationID]: conversation,
    };
    await store.set("conversations", {
      rows: updatedConversations,
      version: 1,
    });
    logger.debug(
      "saved conversations",
      Object.keys(updatedConversations).length,
    );
  };

  const deleteConversations: DataStorage["deleteConversations"] = async (
    conversationIDs: string[],
  ) => {
    const savedConversations = await loadConversations();
    const updatedConversations = {
      ...savedConversations,
    };
    let deletedCount = 0;
    for (const conversationID of conversationIDs) {
      delete updatedConversations[conversationID];
      deletedCount++;
    }
    await store.set("conversations", {
      rows: updatedConversations,
      version: 1,
    });
    logger.log("deleted conversations", deletedCount);
  };

  // TODO: we do not currently persist metadata to storage -- though we may add this in the future
  // if we want to support offline / faster first load if the list metadata call is slow.

  // const loadMetadatas: DataStorage['loadMetadatas'] = async () => {
  //   const savedMetadatas = await store.get('metadatas');
  //   if (savedMetadatas?.version === 1) {
  //     logger.log('loaded metadatas', savedMetadatas.rows.length);
  //     return savedMetadatas.rows;
  //   } else {
  //     // Ignore old data if the version is not >=1
  //     return [];
  //   }
  // };

  // const saveMetadata: DataStorage['saveMetadata'] = async (
  //   metadata: ConversationMetadata,
  // ) => {
  //   const savedMetadatas = await loadMetadatas();
  //   // update existing metadata with same serverConversationId or add new metadata
  //   const existingMetadataIndex = savedMetadatas.findIndex(
  //     (m) => m.serverConversationId === metadata.serverConversationId,
  //   );
  //   if (existingMetadataIndex !== -1) {
  //     savedMetadatas[existingMetadataIndex] = metadata;
  //   } else {
  //     // insert new metadata to front of array assuming it's newly created
  //     savedMetadatas.unshift(metadata);
  //   }
  //   await store.set('metadatas', {
  //     rows: savedMetadatas,
  //     version: 1,
  //   });
  //   logger.log('saved metadatas', savedMetadatas.length);
  // };

  // const deleteMetadatas: DataStorage['deleteMetadatas'] = async (
  //   serverConversationIDs: string[],
  // ) => {
  //   const existingMetadatas = await loadMetadatas();
  //   const updatedMetadatas = existingMetadatas.filter(
  //     (m) => !serverConversationIDs.includes(m.serverConversationId),
  //   );
  //   await store.set('metadatas', {
  //     rows: updatedMetadatas,
  //     version: 1,
  //   });
  //   logger.log(
  //     'deleted metadatas',
  //     existingMetadatas.length - updatedMetadatas.length,
  //   );
  // };

  return {
    loadConversations,
    saveConversation,
    deleteConversations,
  };
}

// TODO: change types to avoid using Date types in the storage objects
function fixConversationsDates(rows: {
  [conversationID: string]: StoredConversation;
}) {
  for (const conversation of Object.values(rows)) {
    for (const message of conversation.messages) {
      if (typeof message.timestamp === "string") {
        message.timestamp = new Date(message.timestamp);
      }
    }
  }
  return rows;
}

/**
 * Only return the most recent conversations (to keep the cache small).
 */
function getRecentConversations(rows: {
  [conversationID: string]: StoredConversation;
}) {
  const recentConversationIDs = new Set<string>();

  // keep conversations that were added to the map recently (at the end)
  const recentEntries = Object.entries(rows).slice(-10);
  for (const entry of recentEntries) {
    recentConversationIDs.add(entry[0]);
  }

  // also keep conversations with the latest messages
  const conversationsWithRecentMessages = Object.entries(rows)
    .sort((aEntry, bEntry) => {
      const aLastMessage = aEntry[1].messages[aEntry[1].messages.length - 1];
      const aLastUpdated = aLastMessage?.timestamp?.getTime() || 0;
      const bLastMessage = bEntry[1].messages[bEntry[1].messages.length - 1];
      const bLastUpdated = bLastMessage?.timestamp?.getTime() || 0;
      return bLastUpdated - aLastUpdated;
    })
    .slice(0, 10);
  for (const entry of conversationsWithRecentMessages) {
    recentConversationIDs.add(entry[0]);
  }

  // return a copy of rows with only the recent conversations (keeping original order)
  const ret: { [conversationID: string]: StoredConversation } = {};
  for (const conversationID of recentConversationIDs) {
    ret[conversationID] = rows[conversationID];
  }
  return ret;
}
